import React, { useCallback, useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import "./Navbar.scss";
import Logo1 from "../components/images/Logo1.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCartShopping } from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useLocation } from "react-router-dom";

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isCheckout = location.pathname.startsWith("/checkout");
  const isAdminPage = location.pathname.startsWith("/admin");
  const isContactPage = location.pathname.startsWith("/contact");
  const [isOpen, setIsOpen] = useState(false);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const [cartQty, setCartQty] = useState(0);
  const [toastMsg, setToastMsg] = useState("");
  const cartBtnRef = useRef(null);
  const hasInit = useRef(false);
  const triggerBounce = useCallback(() => {
    const btn = cartBtnRef.current;
    if (!btn) return;
    btn.classList.remove("is-bounce");
    // force reflow to restart the animation
    void btn.offsetWidth;
    btn.classList.add("is-bounce");
    window.clearTimeout(triggerBounce._t);
    triggerBounce._t = window.setTimeout(() => {
      try {
        btn.classList.remove("is-bounce");
      } catch (_) {}
    }, 600);
  }, []);
  const showToast = (msg) => {
    setToastMsg(msg);
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToastMsg(""), 1800);
  };

  const handleNavClick = (id) => {
    const scrollToId = () => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    };

    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      setTimeout(scrollToId, 60);
    } else {
      scrollToId();
    }
    setIsOpen(false);
  };

  const toggleLanguage = () => {
    i18n.changeLanguage(i18n.language === "en" ? "ar" : "en");
    // Close the hamburger menu (if open) when toggling language
    setIsOpen(false);
  };

  useEffect(() => {
    const readAdminSession = () => {
      try {
        setIsAdminLoggedIn(
          window.sessionStorage.getItem("shawarmaDoseAdmin") === "true"
        );
      } catch (_) {
        setIsAdminLoggedIn(false);
      }
    };

    const handleAdminAuth = (event) => {
      setIsAdminLoggedIn(Boolean(event.detail?.isLoggedIn));
    };

    readAdminSession();
    window.addEventListener("admin:auth", handleAdminAuth);
    window.addEventListener("storage", readAdminSession);

    return () => {
      window.removeEventListener("admin:auth", handleAdminAuth);
      window.removeEventListener("storage", readAdminSession);
    };
  }, []);

  useEffect(() => {
    const highlightActiveSection = () => {
      // Route links use their React class; section highlighting only applies at home.
      if (window.location.pathname !== "/") return;

      document
        .querySelectorAll(".nav-links a")
        .forEach((a) => a.classList.remove("active"));

      const sections = ["home", "menu", "gallery", "location"];
      const scrollPosition = window.scrollY + window.innerHeight;

      for (const id of sections) {
        const section = document.getElementById(id);
        const link = document.querySelector(
          `a[href="#${id}"], a[href="/#${id}"]`
        );
        if (section && link) {
          const offsetTop = section.offsetTop;
          const offsetHeight = section.offsetHeight;

          if (
            scrollPosition >= offsetTop + 100 &&
            scrollPosition <= offsetTop + offsetHeight + 100
          ) {
            link.classList.add("active");
          }
        }
      }
    };

    const handleScroll = () => {
      const navbar = document.querySelector(".navbar");
      if (window.scrollY > 50) {
        navbar.classList.add("scrolled");
      } else {
        navbar.classList.remove("scrolled");
      }
      highlightActiveSection();
    };

    window.addEventListener("scroll", handleScroll);
    document.documentElement.style.scrollBehavior = "smooth";

    // Run once on mount and also when the route changes (e.g., /checkout)
    highlightActiveSection();
    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.documentElement.style.scrollBehavior = "";
    };
  }, [location.pathname]);

  // Listen to quantity updates from Menu.js / basket
  useEffect(() => {
    const cartHandler = (e) => {
      const total = e.detail?.total ?? 0;
      setCartQty((prev) => {
        if (hasInit.current && total > prev) triggerBounce();
        return total;
      });
      hasInit.current = true;
    };
    const basketHandler = (e) => {
      const total = e.detail?.totalQty ?? 0;
      setCartQty((prev) => {
        if (hasInit.current && total > prev) triggerBounce();
        return total;
      });
      hasInit.current = true;
    };

    window.addEventListener("cart:update", cartHandler);
    window.addEventListener("basket:update", basketHandler);

    if (typeof window !== "undefined") {
      if (window.basketItem && typeof window.basketItem.totalQty === "number") {
        setCartQty(window.basketItem.totalQty);
        hasInit.current = true;
      } else if (typeof window.__cartTotal === "number") {
        setCartQty(window.__cartTotal);
        hasInit.current = true;
      }
    }

    return () => {
      window.removeEventListener("cart:update", cartHandler);
      window.removeEventListener("basket:update", basketHandler);
    };
  }, [triggerBounce]);

  return (
    <nav className="navbar">
      {isOpen && (
        <div className="menu-overlay active" onClick={() => setIsOpen(false)} />
      )}
      {/* <div className="logo">
        <img src={Logo1} alt="Shawarma Dose logo" />
        <span className="brand">Shawarma&nbsp;Dose</span>
      </div> */}
      <a
        href="/#home"
        className="logo"
        onClick={(e) => {
          e.preventDefault();
          handleNavClick("home");
        }}
        aria-label="Go to Home"
      >
        <img src={Logo1} alt="Shawarma Dose logo" width="72" height="143" />
        <span className="brand">Shawarma&nbsp;Dose</span>
      </a>
      <ul className={`nav-links ${isOpen ? "open" : "notOpen"}`}>
        <li>
          <a
            href="/#home"
            onClick={(e) => {
              e.preventDefault();
              handleNavClick("home");
            }}
          >
            {t("nav.home")}
          </a>
        </li>
        <li>
          <a
            href="/#menu"
            onClick={(e) => {
              e.preventDefault();
              handleNavClick("menu");
            }}
          >
            {t("nav.menu")}
          </a>
        </li>
        <li>
          <a
            href="/#gallery"
            onClick={(e) => {
              e.preventDefault();
              handleNavClick("gallery");
            }}
          >
            {t("nav.gallery")}
          </a>
        </li>
        <li>
          <a
            href="/#location"
            onClick={(e) => {
              e.preventDefault();
              handleNavClick("location");
            }}
          >
            {t("nav.location")}
          </a>
        </li>
        <li>
          <a
            className={isContactPage ? "active" : ""}
            href="/contact"
            onClick={(e) => {
              e.preventDefault();
              navigate("/contact");
              setIsOpen(false);
            }}
          >
            {t("nav.contact")}
          </a>
        </li>
        {isAdminLoggedIn ? (
          <li>
            <a
              className={isAdminPage ? "active" : ""}
              href="/admin"
              onClick={(e) => {
                e.preventDefault();
                navigate("/admin");
                setIsOpen(false);
              }}
            >
              Admin
            </a>
          </li>
        ) : null}
        <li>
          <button onClick={toggleLanguage}>
            {i18n.language === "en" ? "العربية" : "English"}
          </button>
        </li>
      </ul>

      <div className="nav-actions">
        <button
          ref={cartBtnRef}
          className={`cart-btn ${cartQty === 0 ? "is-disabled" : ""} ${
            isCheckout ? "is-active" : ""
          }`}
          aria-label="Cart"
          title={cartQty === 0 ? "Your cart is empty" : "Go to checkout"}
          onClick={() => {
            if (cartQty > 0) {
              navigate("/checkout");
            } else {
              showToast("Add an item first");
            }
          }}
        >
          <FontAwesomeIcon icon={faCartShopping} className="cart-icon" />
          <span className="cart-badge">{cartQty}</span>
        </button>

        <div
          className={`hamburger ${isOpen ? "open" : ""}`}
          onClick={() => setIsOpen(!isOpen)}
        >
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
      {toastMsg && <div className="toast">{toastMsg}</div>}
    </nav>
  );
};

export default Navbar;
